<script setup lang="ts">
import { map, pickBy, uniqueId } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { getBorrowerDisplayName } from '@/helpers/builder';
import type { ApplicantEntry } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';

interface OwnershipEntry {
  id: string;
  borrowerGroupId: string;
  share: number | undefined;
}

const props = withDefaults(
  defineProps<{
    modelValue: Record<string, number>;
    borrowerGroups: BorrowerGroup[];
    applicants: ApplicantEntry[];
    error?: boolean;
    autoAddBorrowerIds?: string[];
  }>(),
  {
    error: false,
    autoAddBorrowerIds: () => [],
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: Record<string, number>): void;
  (e: 'add-borrower'): void;
}>();

const ownershipEntries = ref<OwnershipEntry[]>([]);

// Flag to track if we're emitting changes (to avoid reinitialization from our own updates)
const isEmitting = ref(false);

// Initialize from modelValue (only for external changes)
const initializeFromModelValue = () => {
  if (isEmitting.value) {
    isEmitting.value = false;
    return;
  }

  const entries = map(
    pickBy(props.modelValue, (share, key) => share !== 0 && props.borrowerGroups.some((g) => g.id === key)),
    (share, borrowerGroupId) => ({ id: uniqueId(), borrowerGroupId, share }),
  );

  ownershipEntries.value = entries;
};

// Watch for external modelValue changes
watch(
  () => props.modelValue,
  () => {
    initializeFromModelValue();
  },
  { immediate: true, deep: true },
);
// Emit updated modelValue using borrower group IDs as keys
// AFTER
const emitChanges = (explicitlyRemovedGroupId?: string) => {
  const newValue: Record<string, number> = {};

  for (const entry of ownershipEntries.value) {
    if (entry.borrowerGroupId && entry.share !== undefined) {
      newValue[entry.borrowerGroupId] = entry.share;
    }
  }

  // Re-emit 0-markers for previously-removed owners so they're not lost on subsequent changes
  for (const [key, value] of Object.entries(props.modelValue)) {
    if (value === 0 && !(key in newValue) && props.borrowerGroups.some((g) => g.id === key)) {
      newValue[key] = 0;
    }
  }

  // Store 0 for a newly removed owner to mark them as explicitly excluded
  if (explicitlyRemovedGroupId && !(explicitlyRemovedGroupId in newValue)) {
    newValue[explicitlyRemovedGroupId] = 0;
  }

  isEmitting.value = true;
  emit('update:modelValue', newValue);
};
// Watch for autoAddBorrowerIds and CREATE entries
watch(
  () => props.autoAddBorrowerIds,
  (newIds) => {
    if (newIds && newIds.length > 0) {
      let hasChanges = false;

      for (const borrowerGroupId of newIds) {
        const alreadyExists = ownershipEntries.value.some((e) => e.borrowerGroupId === borrowerGroupId);
        if (!alreadyExists) {
          // Get the default share from modelValue if it exists
          const defaultShare = props.modelValue[borrowerGroupId];

          // Create the ownership entry automatically
          ownershipEntries.value.push({
            id: uniqueId(),
            borrowerGroupId,
            share: defaultShare,
          });
          hasChanges = true;
        }
      }

      // If we added new entries, emit changes to update parent
      if (hasChanges) {
        emitChanges();
      }
    }
  },
  { immediate: true, deep: true },
);

// Get available borrowers for dropdown (excluding already selected ones)
const getAvailableBorrowers = (currentEntryIndex: number) => {
  const selectedIds = ownershipEntries.value
    .filter((_, index) => index !== currentEntryIndex)
    .map((entry) => entry.borrowerGroupId)
    .filter(Boolean);

  return props.borrowerGroups.filter((group) => !selectedIds.includes(group.id));
};

const getBorrowerOptions = (currentEntryIndex: number): Record<string, string> => {
  const available = getAvailableBorrowers(currentEntryIndex);
  const options: Record<string, string> = {};

  for (const group of available) {
    const displayName = getBorrowerDisplayName(group, props.applicants);
    if (displayName) {
      options[group.id] = displayName;
    }
  }

  return options;
};

const canAddOwner = computed(() => !ownershipEntries.value.some((e) => !e.borrowerGroupId));

/**
 * Appends a new empty owner row. The row's dropdown shows remaining
 * borrower groups plus the "+ Add additional borrower" footer which
 * routes the user back to Borrower details.
 */
const addOwner = (): void => {
  ownershipEntries.value.push({
    id: uniqueId(),
    borrowerGroupId: '',
    share: undefined,
  });
};

const onBorrowerChange = (index: number, value: string | Record<string, unknown>) => {
  // BxSelect can emit string or Record, we only care about string values
  const borrowerGroupId = typeof value === 'string' ? value : '';
  ownershipEntries.value[index].borrowerGroupId = borrowerGroupId;
  emitChanges();
};

const removeOwner = (index: number) => {
  const removedEntry = ownershipEntries.value[index];
  ownershipEntries.value.splice(index, 1);
  emitChanges(removedEntry.borrowerGroupId);
};

const onShareChange = (index: number, value: string | undefined) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  const share = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 100) : undefined;

  ownershipEntries.value[index].share = share;
  emitChanges();
};

const onlyNumbers = (event: KeyboardEvent) => {
  if (event.key < '0' || event.key > '9') {
    event.preventDefault();
  }
};
</script>

<template>
  <div class="borrower-ownership-form">
    <div v-for="(entry, index) in ownershipEntries" :key="entry.id" class="ownership-row mb-4 flex items-start gap-4">
      <div class="flex-1">
        <BxSelect
          :id="`borrower-select-${index}`"
          :data-test-id="`ownership-borrower-${index}`"
          :model-value="entry.borrowerGroupId"
          :list-items="getBorrowerOptions(index)"
          class="!mt-0"
          label="Owner"
          label-type="floating"
          placeholder="Select owner..."
          :has-error="error && !entry.borrowerGroupId"
          @update:model-value="onBorrowerChange(index, $event)"
        >
          <template #dropdown-footer>
            <li
              data-test-id="ownership-add-borrower"
              class="cursor-pointer py-2 pl-3 pr-9 text-gray-900 hover:bg-bridgit-royalBlue hover:text-white"
              @click="emit('add-borrower')"
            >
              + Add additional applicant
            </li>
          </template>
        </BxSelect>
      </div>

      <div class="flex-1">
        <BxInput
          class="pt-1"
          label=""
          aria-label="Ownership percentage"
          :data-test-id="`ownership-share-${index}`"
          :maxlength="3"
          :has-error="error"
          :model-value="entry.share?.toString() ?? ''"
          @update:model-value="onShareChange(index, $event)"
          @keypress="onlyNumbers"
        >
          <template #trailing>
            <span class="mx-2 text-gray-400">%</span>
          </template>
        </BxInput>
      </div>

      <button
        type="button"
        :data-test-id="`ownership-remove-${index}`"
        class="mt-2 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        @click="removeOwner(index)"
      >
        <BxIcon name="cross" class="h-4 w-4" />
      </button>
    </div>
  </div>

  <button
    type="button"
    data-test-id="ownership-add"
    :disabled="!canAddOwner"
    class="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
    :class="{ 'cursor-not-allowed opacity-50': !canAddOwner }"
    @click="addOwner"
  >
    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    Add owner
  </button>
</template>
